import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Treemap
} from 'recharts';
import { FiZap, FiTarget, FiTrendingUp, FiUsers, FiLayers, FiActivity, FiAward, FiClock, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

/* ── CONQUER Theme Colors ── */
const COLORS = {
  primary: '#0891B2',
  secondary: '#06B6D4',
  accent: '#22D3EE',
  dark: '#164E63',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  pink: '#EC4899',
  slate: '#64748B',
  bg: '#F8FAFC',
};

const CHANNEL_COLORS = ['#0891B2', '#06B6D4', '#22D3EE', '#67E8F9', '#164E63', '#8B5CF6', '#EC4899', '#F59E0B'];

/* ── Glass Tooltip ── */
const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)',
      border: '1px solid rgba(8,145,178,0.2)', borderRadius: 16,
      padding: '14px 18px', boxShadow: '0 20px 40px rgba(8,145,178,0.15)',
    }}>
      <p style={{ color: COLORS.dark, fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: 'Inter' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.color || p.fill }} />
          <span style={{ color: COLORS.slate, fontSize: 12, fontWeight: 500 }}>{p.name}:</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.dark }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Metric Card Component ── */
const MetricCard = ({ icon, value, label, trend, color, subtext }) => (
  <div style={{
    background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
    borderRadius: 20, padding: 24, position: 'relative', overflow: 'hidden',
    border: `1px solid ${color}20`, transition: 'all 0.3s ease',
  }}>
    <div style={{
      position: 'absolute', top: -20, right: -20,
      width: 100, height: 100, borderRadius: '50%',
      background: `${color}10`,
    }} />
    <div style={{
      width: 48, height: 48, borderRadius: 14,
      background: `linear-gradient(135deg, ${color}, ${color}CC)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 16, boxShadow: `0 8px 20px ${color}40`,
    }}>
      {React.cloneElement(icon, { size: 22, color: '#fff' })}
    </div>
    <div style={{ fontSize: 32, fontWeight: 800, color: COLORS.dark, fontFamily: 'Inter', letterSpacing: -1 }}>
      {value}
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.slate, marginTop: 4 }}>{label}</div>
    {trend !== undefined && (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8,
        padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
        background: trend >= 0 ? '#10B98120' : '#EF444420',
        color: trend >= 0 ? COLORS.success : COLORS.danger,
      }}>
        <FiTrendingUp style={{ transform: trend < 0 ? 'rotate(180deg)' : 'none' }} />
        {Math.abs(trend)}% vs last week
      </div>
    )}
    {subtext && <div style={{ fontSize: 11, color: COLORS.slate, marginTop: 6 }}>{subtext}</div>}
  </div>
);

/* ── Section Title ── */
const SectionTitle = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
    <div style={{
      width: 44, height: 44, borderRadius: 12,
      background: `linear-gradient(135deg, ${COLORS.primary}20, ${COLORS.accent}15)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: COLORS.primary,
    }}>
      {icon}
    </div>
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, margin: 0, fontFamily: 'Inter' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: COLORS.slate, margin: 0, fontWeight: 500 }}>{subtitle}</p>}
    </div>
  </div>
);

/* ── Activity Item ── */
const ActivityItem = ({ action, user, time, isNew }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0',
    borderBottom: '1px solid #E2E8F0',
  }}>
    <div style={{
      width: 10, height: 10, borderRadius: '50%', marginTop: 5,
      background: isNew ? COLORS.secondary : '#CBD5E1',
      boxShadow: isNew ? `0 0 0 4px ${COLORS.secondary}20` : 'none',
    }} />
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark }}>{action}</div>
      <div style={{ fontSize: 12, color: COLORS.slate, marginTop: 2 }}>{user}</div>
    </div>
    <div style={{ fontSize: 11, color: COLORS.slate, fontWeight: 500 }}>{time}</div>
  </div>
);

/* ── Progress Ring ── */
const ProgressRing = ({ percent, size = 140, strokeWidth = 12, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none" stroke={color}
          strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.dark }}>{percent}%</div>
        <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.slate, textTransform: 'uppercase', letterSpacing: 1 }}>Success</div>
      </div>
    </div>
  );
};

/* ── Treemap Content ── */
const TreemapContent = ({ x, y, width, height, name, value, color }) => {
  if (width < 50 || height < 30) return null;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={color} rx={8} style={{ stroke: '#fff', strokeWidth: 2 }} />
      <text x={x + width/2} y={y + height/2 - 6} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={700}>{name}</text>
      <text x={x + width/2} y={y + height/2 + 10} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize={10}>{value?.toLocaleString()}</text>
    </g>
  );
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 50, height: 50, border: '4px solid #E2E8F0', borderTopColor: COLORS.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (!data) return null;

  const { stats = {}, dailyStats = [], recentAudit = [], channelBreakdown = [], topCampaigns = [] } = data;

  /* Performance Data */
  const days = [...new Set((dailyStats || []).map(d => d._id?.date))].sort();
  const performanceData = days.map(d => ({
    date: new Date(d).toLocaleDateString('en', { weekday: 'short' }),
    delivered: dailyStats.find(s => s._id?.date === d && s._id?.status === 'sent')?.count || 0,
    failed: dailyStats.find(s => s._id?.date === d && s._id?.status === 'failed')?.count || 0
  }));

  /* Channel Treemap */
  const channelData = (channelBreakdown || []).map((ch, i) => ({
    name: ch._id?.replace('whatsapp_', 'WA ').replace('_', ' ').toUpperCase() || 'Other',
    value: (ch.totalSent || 0) + (ch.totalFailed || 0),
    color: CHANNEL_COLORS[i % CHANNEL_COLORS.length]
  })).filter(c => c.value > 0);

  /* Top Campaigns */
  const campaignData = (topCampaigns || []).slice(0, 5).map(c => ({
    name: (c.name || '').substring(0, 12),
    sent: c.totalSent || 0,
    failed: c.totalFailed || 0,
    total: (c.totalSent || 0) + (c.totalFailed || 0)
  }));

  /* Radar Data */
  const radarData = [
    { metric: 'Delivery', value: stats.totalSent || 0, fullMark: Math.max(stats.totalSent || 1, 100) },
    { metric: 'Campaigns', value: stats.totalCampaigns || 0, fullMark: Math.max(stats.totalCampaigns || 1, 50) },
    { metric: 'Users', value: stats.totalUsers || 0, fullMark: Math.max(stats.totalUsers || 1, 20) },
    { metric: 'Records', value: Math.min(stats.totalMasterData || 0, 100), fullMark: 100 },
    { metric: 'Active', value: stats.activeCampaigns || 0, fullMark: Math.max(stats.activeCampaigns || 1, 10) },
  ];

  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0);
  const successRate = totalMessages > 0 ? Math.round((stats.totalSent / totalMessages) * 100) : 0;

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: COLORS.dark, margin: 0, fontFamily: 'Inter' }}>
            Command Center
          </h1>
          <p style={{ fontSize: 14, color: COLORS.slate, marginTop: 6, fontWeight: 500 }}>
            Real-time broadcast intelligence & analytics
          </p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
          borderRadius: 14, color: '#fff', fontWeight: 700, fontSize: 14,
          boxShadow: `0 8px 24px ${COLORS.primary}40`,
        }}>
          <FiZap size={18} />
          Live Dashboard
        </div>
      </div>

      {/* Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 28 }}>
        <MetricCard
          icon={<FiTarget />} value={(stats.totalCampaigns || 0).toLocaleString()}
          label="Total Campaigns" color={COLORS.primary}
          subtext={`${stats.activeCampaigns || 0} currently active`}
        />
        <MetricCard
          icon={<FiCheckCircle />} value={(stats.totalSent || 0).toLocaleString()}
          label="Messages Delivered" color={COLORS.success}
          trend={12}
        />
        <MetricCard
          icon={<FiAlertTriangle />} value={(stats.totalFailed || 0).toLocaleString()}
          label="Failed Deliveries" color={COLORS.danger}
        />
        <MetricCard
          icon={<FiUsers />} value={stats.totalUsers || 0}
          label="Team Members" color={COLORS.purple}
          subtext={`${(stats.totalMasterData || 0).toLocaleString()} data records`}
        />
      </div>

      {/* Main Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 28 }}>
        {/* Performance Chart */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
        }}>
          <SectionTitle icon={<FiTrendingUp size={20} />} title="Performance Analytics" subtitle="7-day delivery metrics" />
          {performanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={performanceData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="deliveredGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={COLORS.secondary} stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: COLORS.slate, fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: COLORS.slate, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Legend wrapperStyle={{ paddingTop: 20 }} formatter={(v) => <span style={{ color: COLORS.dark, fontWeight: 600 }}>{v}</span>} />
                <Bar dataKey="delivered" name="Delivered" fill="url(#deliveredGrad)" radius={[8, 8, 0, 0]} barSize={32} />
                <Line type="monotone" dataKey="failed" name="Failed" stroke={COLORS.danger} strokeWidth={3} dot={{ r: 5, fill: '#fff', stroke: COLORS.danger, strokeWidth: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: COLORS.slate }}>
              <FiActivity size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
              <div style={{ fontWeight: 600 }}>No performance data yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Start sending campaigns to see analytics</div>
            </div>
          )}
        </div>

        {/* Success Rate Ring */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <SectionTitle icon={<FiAward size={20} />} title="Success Rate" subtitle="Overall delivery performance" />
          <ProgressRing
            percent={successRate}
            color={successRate >= 90 ? COLORS.success : successRate >= 70 ? COLORS.warning : COLORS.danger}
          />
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: COLORS.slate, fontWeight: 500 }}>
              {(stats.totalSent || 0).toLocaleString()} delivered of {totalMessages.toLocaleString()} total
            </div>
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, marginBottom: 28 }}>
        {/* Channel Distribution */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
        }}>
          <SectionTitle icon={<FiLayers size={20} />} title="Channel Mix" subtitle="Distribution by type" />
          {channelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <Treemap data={channelData} dataKey="value" nameKey="name" content={<TreemapContent />} animationDuration={800} />
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.slate }}>
              <div style={{ textAlign: 'center' }}>
                <FiLayers size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontWeight: 600 }}>No channel data</div>
              </div>
            </div>
          )}
        </div>

        {/* System Radar */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
        }}>
          <SectionTitle icon={<FiActivity size={20} />} title="System Health" subtitle="Resource overview" />
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: COLORS.slate, fontSize: 11, fontWeight: 600 }} />
              <PolarRadiusAxis angle={30} domain={[0, 'dataMax']} tick={false} axisLine={false} />
              <Radar name="Status" dataKey="value" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.4} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Campaigns */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: 24,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
        }}>
          <SectionTitle icon={<FiTarget size={20} />} title="Top Campaigns" subtitle="Best performers" />
          {campaignData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={campaignData} layout="vertical" margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fill: COLORS.slate, fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: COLORS.dark, fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} width={70} />
                <Tooltip content={<GlassTooltip />} />
                <Bar dataKey="sent" name="Sent" fill={COLORS.primary} radius={[0, 6, 6, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.slate }}>
              <div style={{ textAlign: 'center' }}>
                <FiTarget size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontWeight: 600 }}>No campaigns yet</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Feed */}
      <div style={{
        background: '#fff', borderRadius: 20, padding: 24,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #E2E8F0',
      }}>
        <SectionTitle icon={<FiClock size={20} />} title="Activity Stream" subtitle="Recent operations" />
        {recentAudit?.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0 }}>
            {recentAudit.slice(0, 8).map((log, i) => (
              <ActivityItem
                key={i}
                action={log.action}
                user={`${log.username} — ${log.details}`}
                time={formatTime(log.createdAt)}
                isNew={i < 2}
              />
            ))}
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.slate }}>
            <FiClock size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div style={{ fontWeight: 600 }}>No recent activity</div>
          </div>
        )}
      </div>
    </div>
  );
}
